import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Camera, Check, X, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { resizePhotoToDataUrl } from "@/pages/shoots/shootHelpers";

/** Equipment Admin — CRUD categories + items, inline photo upload (resize to
 *  400 px JPEG on the client), delete. Shoot Team + Super Admin/Admins only. */
export default function EquipmentAdmin() {
  const { user } = useAuth();
  const canView = !!user && (user.is_super_admin || user.is_admin || user.team === "Shoot Team");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState("");
  const [newCatItem, setNewCatItem] = useState("");
  const [newCatQty, setNewCatQty] = useState(1);
  const [addingItem, setAddingItem] = useState(null); // category name being edited
  const [itemDraft, setItemDraft] = useState({ name: "", qty: 1 });
  const [editingItem, setEditingItem] = useState(null); // {id, name, qty}
  const fileInputRefs = useRef({});
  // Drag-to-reorder categories — persists via bulk `POST reorder` endpoint.
  const [dragCatId, setDragCatId] = useState(null);
  const [dragOverCatId, setDragOverCatId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([
        api.get("/shoots/equipment/categories"),
        api.get("/shoots/equipment/items"),
      ]);
      setCats(c.data || []);
      setItems(i.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load equipment");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (canView) load(); }, [canView]);

  // Reorder categories: reshuffle local state so the UI feels instant, then
  // persist the new sequence to `/reorder`. Reverts on failure.
  const reorderCategories = async (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const prev = cats;
    const fromIdx = cats.findIndex((c) => c.id === fromId);
    const toIdx = cats.findIndex((c) => c.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...cats];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setCats(next);
    try {
      await api.post("/shoots/equipment/categories/reorder", { ids: next.map((c) => c.id) });
      toast.success("Order saved");
    } catch (err) {
      setCats(prev);
      toast.error(formatApiError(err.response?.data?.detail) || "Reorder failed — reverted");
    }
  };

  const createCategory = async () => {
    if (!newCat.trim() || !newCatItem.trim()) return toast.error("Category and first item are required");
    try {
      await api.post("/shoots/equipment/categories", { name: newCat.trim() });
      await api.post("/shoots/equipment/items", { category: newCat.trim(), name: newCatItem.trim(), default_qty: newCatQty });
      setNewCat(""); setNewCatItem(""); setNewCatQty(1);
      await load();
      toast.success("Category added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add category");
    }
  };

  const addItem = async (cat) => {
    if (!itemDraft.name.trim()) return toast.error("Item name required");
    try {
      await api.post("/shoots/equipment/items", { category: cat, name: itemDraft.name.trim(), default_qty: itemDraft.qty });
      setAddingItem(null); setItemDraft({ name: "", qty: 1 });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add item");
    }
  };

  const saveItemEdit = async () => {
    if (!editingItem?.id) return;
    try {
      await api.patch(`/shoots/equipment/items/${editingItem.id}`, { name: editingItem.name.trim(), default_qty: editingItem.qty });
      setEditingItem(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update");
    }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`/shoots/equipment/items/${it.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" and all its items?`)) return;
    try {
      await api.delete(`/shoots/equipment/categories/${cat.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const uploadPhoto = async (it, file) => {
    if (!file) return;
    try {
      const photo_url = await resizePhotoToDataUrl(file);
      await api.patch(`/shoots/equipment/items/${it.id}`, { photo_url });
      toast.success("Photo saved");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Photo upload failed");
    }
  };

  if (!canView) {
    return <div className="p-6 text-white/60">Shoot Team access only.</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="equipment-admin-page">
      <div>
        <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2">
          <Package size={12} /> Equipment
        </div>
        <h1 className="text-3xl sm:text-4xl font-display tracking-tight">Studio kit master list</h1>
        <p className="text-sm text-white/50 mt-1">Categories & items available for every pre-shoot checklist. Drag any category card by the grip to reorder — the sequence saves automatically.</p>
      </div>

      {/* Add category */}
      <div className="rounded-lg border border-red-500/40 bg-red-500/[0.03] p-4">
        <div className="text-[0.7rem] uppercase tracking-widest text-red-300 mb-2">Add new category</div>
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1.4fr_100px_auto] gap-2">
          <Input placeholder="Category name (e.g. Lens)" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="equipment-newcat-name" className="bg-black/40 border-white/10" />
          <Input placeholder="First item (e.g. 24-70mm f/2.8)" value={newCatItem} onChange={(e) => setNewCatItem(e.target.value)} data-testid="equipment-newcat-item" className="bg-black/40 border-white/10" />
          <Input type="number" min="1" placeholder="Qty" value={newCatQty} onChange={(e) => setNewCatQty(parseInt(e.target.value, 10) || 1)} data-testid="equipment-newcat-qty" className="bg-black/40 border-white/10" />
          <Button onClick={createCategory} className="bg-[#E11D2E] hover:bg-[#F03041]" data-testid="equipment-newcat-submit">
            <Plus size={14} className="mr-1.5" /> Add
          </Button>
        </div>
      </div>

      {loading && <div className="text-white/50 text-sm">Loading…</div>}

      {/* Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {cats.map((cat) => {
          const list = items.filter((i) => i.category === cat.name);
          const isDragOver = dragOverCatId === cat.id && dragCatId !== cat.id;
          return (
            <div
              key={cat.id}
              draggable
              onDragStart={() => setDragCatId(cat.id)}
              onDragEnd={() => { setDragCatId(null); setDragOverCatId(null); }}
              onDragOver={(e) => { if (dragCatId) { e.preventDefault(); setDragOverCatId(cat.id); } }}
              onDragLeave={() => setDragOverCatId((cur) => (cur === cat.id ? null : cur))}
              onDrop={(e) => { e.preventDefault(); reorderCategories(dragCatId, cat.id); }}
              className={`rounded-lg border bg-black/25 p-4 transition ${isDragOver ? "border-emerald-400/60 ring-2 ring-emerald-400/40" : "border-white/10"} ${dragCatId === cat.id ? "opacity-60" : ""}`}
              data-testid={`equipment-cat-${cat.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg flex items-center gap-2">
                  <span className="cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60" title="Drag to reorder" data-testid={`equipment-cat-grip-${cat.id}`}>
                    <GripVertical size={14} />
                  </span>
                  {cat.name}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-[0.7rem] text-white/40 font-mono">{list.length} items</span>
                  <button onClick={() => removeCategory(cat)} title="Delete category" data-testid={`equipment-cat-delete-${cat.id}`} className="h-7 w-7 rounded flex items-center justify-center text-red-300/70 hover:bg-red-500/10">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {list.map((it) => {
                  const editing = editingItem?.id === it.id;
                  return (
                    <div key={it.id} className="flex items-center gap-3 py-2" data-testid={`equipment-item-${it.id}`}>
                      <div className="relative">
                        {it.photo_url ? (
                          <img src={it.photo_url} alt="" className="h-10 w-10 rounded object-cover border border-white/10" />
                        ) : (
                          <div className="h-10 w-10 rounded border border-dashed border-white/20 flex items-center justify-center text-white/30">
                            <Camera size={12} />
                          </div>
                        )}
                        <input
                          ref={(el) => (fileInputRefs.current[it.id] = el)}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => uploadPhoto(it, e.target.files?.[0])}
                        />
                        <button
                          onClick={() => fileInputRefs.current[it.id]?.click()}
                          className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-[#E11D2E] flex items-center justify-center text-[0.6rem] text-white"
                          title="Add / replace photo"
                          data-testid={`equipment-item-photo-${it.id}`}
                        >+</button>
                      </div>
                      {editing ? (
                        <>
                          <Input value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="bg-black/40 border-white/10 h-8 flex-1" />
                          <Input type="number" min="1" value={editingItem.qty} onChange={(e) => setEditingItem({ ...editingItem, qty: parseInt(e.target.value, 10) || 1 })} className="bg-black/40 border-white/10 h-8 w-16" />
                          <button onClick={saveItemEdit} className="h-7 w-7 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 flex items-center justify-center" title="Save"><Check size={13} /></button>
                          <button onClick={() => setEditingItem(null)} className="h-7 w-7 rounded bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60" title="Cancel"><X size={13} /></button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 truncate">
                            <div className="text-sm">{it.name}</div>
                            <div className="text-[0.7rem] text-white/40 font-mono">default qty {it.default_qty}</div>
                          </div>
                          <button onClick={() => setEditingItem({ id: it.id, name: it.name, qty: it.default_qty })} className="h-7 w-7 rounded bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60" title="Edit" data-testid={`equipment-item-edit-${it.id}`}><Pencil size={12} /></button>
                          <button onClick={() => removeItem(it)} className="h-7 w-7 rounded bg-red-500/10 text-red-300 hover:bg-red-500/20 flex items-center justify-center" title="Delete" data-testid={`equipment-item-delete-${it.id}`}><Trash2 size={12} /></button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Add item */}
              {addingItem === cat.name ? (
                <div className="mt-3 flex gap-2">
                  <Input placeholder="Item name" value={itemDraft.name} onChange={(e) => setItemDraft({ ...itemDraft, name: e.target.value })} className="bg-black/40 border-white/10 h-8 flex-1" data-testid={`equipment-additem-name-${cat.id}`} />
                  <Input type="number" min="1" value={itemDraft.qty} onChange={(e) => setItemDraft({ ...itemDraft, qty: parseInt(e.target.value, 10) || 1 })} className="bg-black/40 border-white/10 h-8 w-20" />
                  <Button onClick={() => addItem(cat.name)} className="h-8 bg-[#E11D2E] hover:bg-[#F03041]" data-testid={`equipment-additem-save-${cat.id}`}>Save</Button>
                  <Button variant="ghost" onClick={() => setAddingItem(null)} className="h-8">Cancel</Button>
                </div>
              ) : (
                <button
                  onClick={() => { setAddingItem(cat.name); setItemDraft({ name: "", qty: 1 }); }}
                  className="mt-3 text-xs text-white/50 hover:text-white flex items-center gap-1"
                  data-testid={`equipment-additem-btn-${cat.id}`}
                ><Plus size={12} /> Add item</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
